import api from '../axios.config';
import {
  AttemptView,
  AutoRechargeEventDTO,
  AutoRechargeSettingsBody,
  AutoRechargeStatus,
  GatewayAccountDTO,
  GatewayAccountUpsert,
  GatewayTestResult,
  MandateRequest,
  MandateView,
} from '../../types/autorecharge';
import { SpringPage } from '../../types/billing';

/**
 * Wallet auto-recharge (per organisation) and payment gateway accounts (master only).
 * Kept apart from billing.service so the "My billing" page can reuse it as-is.
 */
export const AUTORECHARGE_ENDPOINTS = {
  STATUS: (org: string) => `/billing/autorecharge/organization/${encodeURIComponent(org)}`,
  RESUME: (org: string) => `/billing/autorecharge/organization/${encodeURIComponent(org)}/resume`,
  MANDATES: (org: string) => `/billing/autorecharge/organization/${encodeURIComponent(org)}/mandates`,
  REVOKE: (org: string, id: number) =>
    `/billing/autorecharge/organization/${encodeURIComponent(org)}/mandates/${id}/revoke`,
  ATTEMPTS: (org: string) => `/billing/autorecharge/organization/${encodeURIComponent(org)}/attempts`,
  EVENTS: (org: string) => `/billing/autorecharge/organization/${encodeURIComponent(org)}/events`,
  GATEWAYS: '/billing/payment-gateways',
  GATEWAY: (id: number) => `/billing/payment-gateways/${id}`,
  GATEWAY_TEST: (id: number) => `/billing/payment-gateways/${id}/test`,
};

export interface Result<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const failure = (error: any, fallback: string): string => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;
  if (message && typeof message === 'string') return message;
  if (status === 403) return 'You do not have access to this';
  if (status === 404) return 'Not found';
  if (status === 502) return 'The payment gateway did not answer; try again';
  return fallback;
};

const wrap = async <T>(call: () => Promise<{ data: T }>, fallback: string): Promise<Result<T>> => {
  try {
    const r = await call();
    return { success: true, data: r.data };
  } catch (error: any) {
    return { success: false, error: failure(error, fallback) };
  }
};

export const autoRechargeService = {
  getStatus: (org: string) =>
    wrap<AutoRechargeStatus>(() => api.get(AUTORECHARGE_ENDPOINTS.STATUS(org)), 'Failed to load auto-recharge'),

  update: (org: string, body: AutoRechargeSettingsBody) =>
    wrap<AutoRechargeStatus>(() => api.put(AUTORECHARGE_ENDPOINTS.STATUS(org), body), 'Failed to save auto-recharge'),

  resume: (org: string) =>
    wrap<AutoRechargeStatus>(() => api.post(AUTORECHARGE_ENDPOINTS.RESUME(org)), 'Failed to resume auto-recharge'),

  createMandate: (org: string, body: MandateRequest) =>
    wrap<MandateView>(() => api.post(AUTORECHARGE_ENDPOINTS.MANDATES(org), body), 'Failed to start autopay set-up'),

  revokeMandate: (org: string, id: number) =>
    wrap<MandateView>(() => api.post(AUTORECHARGE_ENDPOINTS.REVOKE(org, id)), 'Failed to revoke the mandate'),

  getAttempts: (org: string, page = 0, size = 10) =>
    wrap<SpringPage<AttemptView>>(
      () => api.get(AUTORECHARGE_ENDPOINTS.ATTEMPTS(org), { params: { page, size } }),
      'Failed to load recharge history',
    ),

  getEvents: (org: string, page = 0, size = 20) =>
    wrap<SpringPage<AutoRechargeEventDTO>>(
      () => api.get(AUTORECHARGE_ENDPOINTS.EVENTS(org), { params: { page, size } }),
      'Failed to load the activity log',
    ),

  listGateways: () =>
    wrap<GatewayAccountDTO[]>(() => api.get(AUTORECHARGE_ENDPOINTS.GATEWAYS), 'Failed to load gateway accounts'),

  createGateway: (body: GatewayAccountUpsert) =>
    wrap<GatewayAccountDTO>(() => api.post(AUTORECHARGE_ENDPOINTS.GATEWAYS, body), 'Failed to create the gateway account'),

  updateGateway: (id: number, body: GatewayAccountUpsert) =>
    wrap<GatewayAccountDTO>(() => api.put(AUTORECHARGE_ENDPOINTS.GATEWAY(id), body), 'Failed to save the gateway account'),

  testGateway: (id: number) =>
    wrap<GatewayTestResult>(() => api.post(AUTORECHARGE_ENDPOINTS.GATEWAY_TEST(id)), 'Failed to test the connection'),
};
